import React from 'react';
import { ArrowLeft, Download, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';

const PDF_URL = '/legal/acuerdo-usuario-autogo.pdf';

const MobileUserAgreement = () => {
  return (
    <div className="min-h-screen bg-white text-gray-800">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 pb-16 [&_p]:text-gray-700 [&_li]:text-gray-700 [&_strong]:text-gray-900">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[#0A4F6E] hover:text-[#39FF77] mb-6 transition-colors text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          autogodelivery.com
        </Link>

        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-8">
          <div>
            <p className="text-sm text-gray-500 mb-1">AutoGo · Versión 1.0 · 2026</p>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#0A4F6E] leading-tight">
              Acuerdo de Usuario, Condiciones de Uso y Política de Privacidad
            </h1>
            <p className="text-sm text-gray-600 mt-2">Aplicación móvil para gestión de entrega de vehículos</p>
          </div>
          <a
            href={PDF_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 shrink-0 px-4 py-2.5 rounded-lg bg-[#0A4F6E] text-white text-sm font-medium hover:bg-[#083d56] transition-colors"
          >
            <Download className="w-4 h-4" />
            Descargar PDF
          </a>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-8 text-sm text-amber-900">
          <p className="flex items-start gap-2">
            <FileText className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              <strong>IMPORTANTE:</strong> Antes de utilizar la aplicación AutoGo, lea detenidamente el presente
              Acuerdo. Al registrarse o utilizar la aplicación, usted acepta expresamente todas las condiciones
              aquí establecidas, incluyendo el tratamiento de sus datos personales conforme al RGPD y la LOPDGDD.
            </span>
          </p>
        </div>

        <article className="prose prose-sm sm:prose-base max-w-none space-y-8">
          <section>
            <h2 className="text-xl font-bold text-[#0A4F6E]">1. Identificación del responsable del tratamiento</h2>
            <ul className="list-none pl-0 space-y-1 text-sm">
              <li><strong>Empresa:</strong> AutoGo – PYRAMID LOGISTICS, S.L</li>
              <li><strong>Sitio web:</strong> www.autogodelivery.com</li>
              <li><strong>Actividad:</strong> Gestión logística y entrega de vehículos</li>
              <li><strong>Normativa:</strong> RGPD (UE) 2016/679 · LOPDGDD 3/2018 · LSSI-CE · Directiva ePrivacy</li>
              <li><strong>DPO / Contacto privacidad:</strong>{' '}
                <a href="mailto:dpo@autogodelivery.com" className="text-[#0A4F6E] hover:underline">dpo@autogodelivery.com</a>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#0A4F6E]">2. Objeto y ámbito de aplicación</h2>
            <p>
              El presente Acuerdo regula el acceso y uso de la aplicación móvil AutoGo (en adelante, &quot;la App&quot;),
              destinada a conductores profesionales y personal autorizado para la gestión, recogida y entrega de vehículos.
            </p>
            <p>Este Acuerdo es vinculante para:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Conductores que utilicen la App en el desempeño de sus funciones.</li>
              <li>Personal de backoffice y supervisores con acceso a la plataforma.</li>
              <li>Cualquier usuario registrado en la aplicación con cualquier rol asignado.</li>
            </ul>
            <p>
              El uso de la App implica la aceptación plena e incondicional de este Acuerdo. Si no está de acuerdo con
              alguna de sus cláusulas, deberá abstenerse de utilizar la aplicación.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#0A4F6E]">3. Datos personales recogidos y finalidades</h2>

            <h3 className="text-lg font-semibold text-gray-900 mt-4">3.1 Fotografías del estado de los vehículos</h3>
            <p>
              La App captura imágenes fotográficas de los vehículos al inicio y fin de cada servicio de recogida o entrega.
              <strong> Finalidad:</strong> documentar el estado del vehículo para proteger a todas las partes frente a
              posibles reclamaciones por daños.
            </p>
            <ul className="list-disc pl-5 text-sm space-y-1">
              <li>Base jurídica: ejecución del contrato (art. 6.1.b RGPD) e interés legítimo (art. 6.1.f RGPD).</li>
              <li>Plazo de conservación: 5 años desde la fecha del servicio.</li>
              <li>Las imágenes pueden contener metadatos de geolocalización y marca temporal.</li>
              <li>Acceso restringido a personal autorizado y al cliente del servicio correspondiente.</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-900 mt-4">3.2 Firma digital del cliente</h3>
            <p>
              La App recoge la firma manuscrita digitalizada del cliente receptor del vehículo mediante la pantalla táctil
              del dispositivo, con validez conforme al Reglamento eIDAS (UE) No 910/2014.
            </p>
            <ul className="list-disc pl-5 text-sm space-y-1">
              <li>Base jurídica: ejecución del contrato y obligación legal.</li>
              <li>Plazo de conservación: 10 años.</li>
              <li>La firma se almacena como imagen cifrada vinculada al albarán de entrega.</li>
              <li>No se utiliza para identificación biométrica ni reconocimiento de identidad.</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-900 mt-4">3.3 Fotografías de los conductores (selfie de verificación)</h3>
            <p>
              La App solicita al conductor una fotografía de sí mismo como verificación de identidad y presencia en el servicio.
            </p>
            <ul className="list-disc pl-5 text-sm space-y-1">
              <li>Base jurídica: ejecución del contrato e interés legítimo.</li>
              <li>Plazo de conservación: 2 años desde la realización del servicio.</li>
              <li>Las imágenes NO son procesadas mediante reconocimiento facial automatizado.</li>
              <li>Acceso restringido exclusivamente a supervisores y personal de seguridad autorizado.</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-900 mt-4">3.4 Rastreo de ubicación GPS en tiempo real</h3>
            <p>
              La App rastrea la ubicación geográfica del conductor exclusivamente mientras tiene un servicio activo en curso.
              El rastreo se detiene de forma automática e inmediata al cerrar el servicio (subida de gastos y cierre).
              Fuera de ese período, la App no captura ni transmite ningún dato de ubicación.
            </p>
            <ul className="list-disc pl-5 text-sm space-y-1">
              <li>Base jurídica: ejecución del contrato e interés legítimo del responsable.</li>
              <li>Los datos se transmiten cifrados mediante TLS 1.3.</li>
              <li>Historial de rutas conservado durante 1 año para fines de facturación y seguridad.</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-900 mt-4">3.4.1 Acceso a la ubicación por el destinatario del vehículo</h3>
            <p>
              El destinatario del vehículo podrá acceder en tiempo real a la ubicación del conductor asignado a su entrega,
              a través de un enlace de seguimiento personalizado facilitado por AutoGo.
            </p>
            <ul className="list-disc pl-5 text-sm space-y-1">
              <li>El enlace es único, temporal y vinculado exclusivamente al servicio de entrega en curso.</li>
              <li>El acceso se desactiva automáticamente al cerrar el servicio, sin período de gracia.</li>
              <li>El destinatario solo ve la posición en el mapa y el ETA; no se facilitan datos personales del conductor.</li>
              <li>El conductor es informado mediante aviso visible en la App durante el trayecto de entrega.</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-900 mt-4">3.5 Otros datos de la cuenta</h3>
            <ul className="list-disc pl-5 text-sm space-y-1">
              <li>Datos identificativos: nombre, apellidos, número de empleado, DNI/NIE.</li>
              <li>Datos de contacto: teléfono, correo electrónico corporativo.</li>
              <li>Datos de acceso: nombre de usuario, contraseña (hash bcrypt).</li>
              <li>Registros de actividad: logs de uso, marcas temporales, dispositivo utilizado.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#0A4F6E]">4. Permisos requeridos por la aplicación</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-gray-200 rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-2 border-b">Permiso</th>
                    <th className="text-left p-2 border-b">Finalidad</th>
                    <th className="text-left p-2 border-b">Obligatorio</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td className="p-2 border-b">Cámara</td><td className="p-2 border-b">Fotografías de vehículos y selfies</td><td className="p-2 border-b">Sí</td></tr>
                  <tr><td className="p-2 border-b">Ubicación precisa</td><td className="p-2 border-b">Rastreo GPS durante servicio activo</td><td className="p-2 border-b">Sí</td></tr>
                  <tr><td className="p-2 border-b">Ubicación en segundo plano</td><td className="p-2 border-b">Continuidad del rastreo con App minimizada</td><td className="p-2 border-b">Sí</td></tr>
                  <tr><td className="p-2 border-b">Almacenamiento</td><td className="p-2 border-b">Guardar imágenes temporalmente</td><td className="p-2 border-b">Sí</td></tr>
                  <tr><td className="p-2 border-b">Internet</td><td className="p-2 border-b">Sincronización con servidores</td><td className="p-2 border-b">Sí</td></tr>
                  <tr><td className="p-2">Notificaciones</td><td className="p-2">Alertas de nuevos servicios</td><td className="p-2">Recomendado</td></tr>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#0A4F6E]">5. Derechos de los usuarios</h2>
            <p>Conforme al RGPD y la LOPDGDD, el usuario dispone de los derechos de acceso, rectificación, supresión, limitación, portabilidad, oposición y a no ser objeto de decisiones automatizadas.</p>
            <p>
              Para ejercer estos derechos, contacte con{' '}
              <a href="mailto:dpo@autogodelivery.com" className="text-[#0A4F6E] hover:underline">dpo@autogodelivery.com</a>
              {' '}adjuntando copia de su DNI/NIE. Respuesta en el plazo máximo de 1 mes. Puede reclamar ante la{' '}
              <a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer" className="text-[#0A4F6E] hover:underline">AEPD</a>.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#0A4F6E]">6. Transferencias internacionales</h2>
            <p>
              El tratamiento se realiza preferentemente dentro del EEE. Para proveedores fuera del EEE se aplican
              Cláusulas Contractuales Tipo, decisiones de adecuación u otras garantías exigidas por el RGPD.
              Lista de subencargados disponible en{' '}
              <a href="mailto:dpo@autogodelivery.com" className="text-[#0A4F6E] hover:underline">dpo@autogodelivery.com</a>.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#0A4F6E]">7. Medidas de seguridad</h2>
            <ul className="list-disc pl-5 text-sm space-y-1">
              <li>Cifrado en tránsito TLS 1.3 y en reposo AES-256 para datos sensibles.</li>
              <li>Autenticación multifactor (MFA) para el panel de administración.</li>
              <li>Control de acceso basado en roles (RBAC).</li>
              <li>Auditorías de seguridad y procedimiento de brechas (notificación AEPD en 72h si aplica).</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#0A4F6E]">8. Cookies y tecnologías de seguimiento</h2>
            <p>
              La App móvil no utiliza cookies en sentido estricto. Utiliza tokens de sesión, identificadores de dispositivo
              y, con consentimiento, analítica de uso anónima. Preferencias en Ajustes &gt; Privacidad.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#0A4F6E]">9. Usuarios menores de edad</h2>
            <p>
              La App está destinada exclusivamente a mayores de 18 años. No se recogen datos de menores de 14 años
              sin consentimiento parental; en caso de detección, se procederá a su eliminación inmediata.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#0A4F6E]">10. Condiciones de uso</h2>
            <p><strong>Obligaciones:</strong> uso profesional autorizado, no compartir credenciales, reportar brechas, cumplir instrucciones de AutoGo.</p>
            <p><strong>Prohibiciones:</strong> fotografías de personas ajenas al servicio, rastreo fuera de servicio activo, ingeniería inversa, uso no autorizado.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#0A4F6E]">11. Vigencia, modificaciones y baja</h2>
            <p>
              Vigente mientras la cuenta esté activa. Modificaciones sustanciales con 30 días de antelación por notificación
              en la App y/o correo. La baja implica cese del tratamiento activo, sin perjuicio de plazos legales de conservación.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#0A4F6E]">12. Legislación aplicable</h2>
            <p>Legislación española y de la Unión Europea. Jurisdicción de los Juzgados del domicilio social de AutoGo.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#0A4F6E]">13. Declaración de aceptación</h2>
            <p>
              Al marcar la casilla de aceptación o pulsar &quot;Acepto las condiciones&quot;, el usuario declara haber leído y
              aceptado este Acuerdo, prestar consentimiento al tratamiento de datos descrito y confirmar ser mayor de 18 años.
              El consentimiento queda registrado con marca temporal y datos del dispositivo (art. 7.1 RGPD).
            </p>
          </section>
        </article>

        <footer className="mt-12 pt-6 border-t border-gray-200 text-sm text-gray-600 text-center">
          <p>AutoGo · PYRAMID LOGISTICS, S.L</p>
          <p>
            <a href="mailto:dpo@autogodelivery.com" className="text-[#0A4F6E] hover:underline">dpo@autogodelivery.com</a>
            {' · '}
            <a href="https://www.autogodelivery.com" className="text-[#0A4F6E] hover:underline">www.autogodelivery.com</a>
          </p>
          <a
            href={PDF_URL}
            className="inline-flex items-center gap-1 mt-3 text-[#0A4F6E] hover:underline"
          >
            <Download className="w-4 h-4" />
            Versión PDF oficial
          </a>
        </footer>
      </div>
    </div>
  );
};

export default MobileUserAgreement;
